// Helpers for the job server pages shown in the detail panels (job ad, applicant CV).
// Their own bottom bar is hidden and its actions move into the panel's left column.
// All of this works only when the page is ours (production, same origin).

export interface FrameLanguage {
  value: string
  label: string
}

// The document of a frame, or null for another origin (local development)
export const frameDocumentOf = (frame: HTMLIFrameElement | null | undefined): Document | null => {
  try {
    return frame?.contentDocument || null;
  } catch {
    return null;
  }
};

// The Google translation languages of the page's select; its first option ("-") only asks to choose
export const readFrameLanguages = (select: HTMLSelectElement | null): { prompt: string, languages: FrameLanguage[] } => {
  if (!select) return { prompt: '', languages: [] };
  const options = Array.from(select.options);
  const languages: FrameLanguage[] = [];
  options.forEach((option) => {
    // Separators are disabled; the partner's language can come twice (on top and in the list)
    if (option.disabled || !option.value || option.value === '-') return;
    if (languages.some((language) => language.value === option.value)) return;
    languages.push({ value: option.value, label: (option.textContent || '').trim() });
  });
  return { prompt: (options[0]?.textContent || '').trim(), languages };
};

// Picks a language in the page's select; its own change handler loads the translation
export const selectFrameLanguage = (select: HTMLSelectElement | null, value: string) => {
  const option = select ? Array.from(select.options).find((item) => item.value === value && !item.disabled) : null;
  if (!select || !option) return;
  option.selected = true;
  select.dispatchEvent(new Event('change'));
};

// Adds a style to the page once (hides its bar)
export const addFrameStyle = (page: Document | null, id: string, css: string) => {
  if (!page?.head || page.getElementById(id)) return;
  const style = page.createElement('style');
  style.id = id;
  style.textContent = css;
  page.head.append(style);
};

// A link of the page's bar shown as a row of the left panel
export interface FrameAction {
  key: string
  index: number
  icon: string
  label: string
  title: string
  newTab: boolean
}

// Bar link classes → panel icons
export const FRAME_ACTION_ICONS: Record<string, string> = {
  'icon-picture': 'bi-wikipedia', // Wikipedia of the place, one per place
  'icon-pin': 'bi-signpost-2', // route planner
  'icon-bus': 'bi-bus-front', // public transport (DB: Bus+Bahn / ÖBB)
};

// bahn.de / oebb.at refuse to be shown in a frame
const NEW_TAB_ICONS = ['icon-bus'];

// "a.icon-picture, a.icon-bus" inside the bar for the given link classes
export const frameActionsSelector = (iconClasses: string[]): string => iconClasses
  .map((name) => `.button-container a.${name}`)
  .join(', ');

export const readFrameActions = (page: Document | null, selector: string): FrameAction[] => {
  if (!page) return [];
  const links = Array.from(page.querySelectorAll(selector)) as HTMLAnchorElement[];
  return links.map((link, index) => {
    const iconClass = Object.keys(FRAME_ACTION_ICONS).find((name) => link.classList.contains(name)) || '';
    return {
      key: `${index}-${link.getAttribute('href')}`,
      index,
      icon: FRAME_ACTION_ICONS[iconClass] || 'bi-box-arrow-up-right',
      label: (link.textContent || '').trim(),
      title: link.getAttribute('title') || '',
      newTab: NEW_TAB_ICONS.includes(iconClass) || link.target === '_blank',
    };
  });
};

// Runs the bar's own link: its page opens in the frame, or in a new tab
export const runFrameAction = (page: Document | null, selector: string, action: FrameAction) => {
  const link = page?.querySelectorAll(selector)[action.index] as HTMLAnchorElement | undefined;
  if (!link) return;
  if (action.newTab) {
    window.open(link.href, '_blank', 'noopener');
    return;
  }
  link.click();
};
